import { useState, useEffect } from "react";
import { useApi } from "../utils/Api";
import Translate from "../utils/Translate";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  CardContent,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BankIdAuthentication from "../components/bankId/BankIdAuthentication";
import Preloader from "../components/Preloader";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Card from "@mui/material/Card";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import { formatDate, UTCToLocalDateOnly } from "../utils/dateTimeFormat";
import PaymentDialog from "../components/checkout/PaymentDialog";

const LostCard = () => {
  const currentYear = new Date().getFullYear();
  let years = arrayRange(currentYear, 1970, -1);

  function arrayRange(start, stop, step) {
    return Array.from({ length: (stop - start) / step + 1 }, (value, index) => start + index * step);
  }

  const LostCardView = Object.freeze({
    INIT: "init",
    BANK_ID: "bankId",
    FORM: "form",
    LICENSE: "license",
    ORDRER_SUMMARY: "orderSummary",
    NOT_FOUND: "notFound",
    PAYMENT: "payment",
    SUCCESS: "success",
    ORDER_SUCCESS: "orderSuccess",
  });

  const api = useApi();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [showView, setShowView] = useState(isSmallScreen ? LostCardView.BANK_ID : LostCardView.INIT);
  const [token, setToken] = useState(null);
  const [actorLicenses, setActorLicenses] = useState([]);
  const [selectedLicenseId, setSelectedLicenseId] = useState(null);
  const [selectedLicenseType, setSelectedLicenseType] = useState("choose");
  const [isLoading, setIsLoading] = useState(false);
  const [langLoaded, setLangLoaded] = useState(false);

  const [name, setName] = useState("");
  const [orgNo, setOrgNo] = useState("");
  const [email, setEmail] = useState("");
  const [education, setEducation] = useState("choose");
  const [freeTextEducation, setFreeTextEducation] = useState("");
  const [yearOfEducation, setYearOfEducation] = useState("choose");
  const [placeOfEducation, setPlaceOfEducation] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [educationProvider, setEducationProvider] = useState("");
  const [deliveryName, setDeliveryName] = useState("");
  const [street, setStreet] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");

  const [orderId, setOrderId] = useState("");
  const [paymentId, setPaymentId] = useState("");

  const [previewStudentName, setPreviewStudentName] = useState("");
  const [previewStudentOrgNo, setPreviewStudentOrgNo] = useState("");
  const [previewValidFrom, setPreviewValidFrom] = useState("");
  const [previewTheoryCompetence, setPreviewTheoryCompetence] = useState([]);
  const [previewPracticalCompetence, setPreviewPracticalCompetence] = useState([]);
  const [previewTeacherName, setPreviewTeacherName] = useState("");
  const [previewCourseCoordinator, setPreviewCourseCoordinator] = useState("");

  const [openCompetencesDialog, setOpenCompetencesDialog] = useState(false);

  useEffect(() => {
    const loadLang = async () => {
      await Translate.loadLostCard();
      setLangLoaded(true);
    };
    loadLang();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const showWarning = !(
        showView === LostCardView.INIT ||
        showView === LostCardView.BANK_ID ||
        showView === LostCardView.SUCCESS ||
        showView === LostCardView.ORDER_SUCCESS
      );

      if (showWarning) {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [showView]);

  useEffect(() => {
    if (selectedLicenseType === "choose") {
      setSelectedLicenseId(null);
      return;
    }

    const matchingLicenses = filterLicensesByType(actorLicenses, selectedLicenseType);
    if (matchingLicenses.length > 0) {
      setSelectedLicenseId(matchingLicenses[0].actorLicenseId);
    } else {
      setSelectedLicenseId(null);
    }
  }, [selectedLicenseType, actorLicenses]);

  function disableSendButton() {
    switch (showView) {
      case LostCardView.FORM:
        return (
          name === "" ||
          email === "" ||
          education === "choose" ||
          (education === "other" && freeTextEducation === "") ||
          yearOfEducation === "choose" ||
          deliveryName === "" ||
          street === "" ||
          zipCode === "" ||
          city === ""
        );

      case LostCardView.LICENSE:
        return (
          name === "" ||
          email === "" ||
          selectedLicenseId == null ||
          deliveryName === "" ||
          street === "" ||
          zipCode === "" ||
          city === ""
        );

      default:
        return false;
    }
  }

  function formatNames(nameString) {
    let words = nameString.split(" ");

    let capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

    return capitalizedWords.join(" ");
  }

  function onAuthenticationSuccess(loginType, response) {
    if (response.data.actorLicenses?.length > 0) {
      setActorLicenses(response.data.actorLicenses);
      setShowView(LostCardView.LICENSE);
    } else if (response.data.actorExists) {
      setShowView(LostCardView.FORM);
    } else {
      setShowView(LostCardView.NOT_FOUND);
    }

    setOrgNo(response.data.ssn);
    setToken(response.data.token);
    setName(formatNames(response.data.givenName) + " " + formatNames(response.data.surName));
  }

  async function handleLostCardSubmit() {
    if (showView === LostCardView.LICENSE) {
      await getLostCardPreview();
    } else {
      await postLostCardRequest();
    }
  }

  async function postLostCardRequest() {
    setIsLoading(true);

    let reqObj = {
      name: name,
      orgNo: orgNo,
      email: email,
      education: education,
      freeTextEducation: freeTextEducation,
      yearOfEducation: yearOfEducation,
      placeOfEducation: placeOfEducation,
      instructorName: instructorName,
      educationProvider: educationProvider,
      deliveryName: deliveryName,
      street: street,
      zipCode: zipCode,
      city: city,
      token: token,
    };

    try {
      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}lostcard`, reqObj, "POST", false, true);
      if (response.isSuccessful) {
        setShowView(LostCardView.SUCCESS);
        clearStates();
      } else if (response.errorMessageTranslationKey === "Unauthorized") {
        setShowView(LostCardView.BANK_ID);
        clearStates();
      }
    } catch (error) {
      setShowView(LostCardView.INIT);
    } finally {
      setIsLoading(false);
    }
  }

  async function postLostCardOrder() {
    setIsLoading(true);

    const selectedLicense = actorLicenses.find((l) => l.actorLicenseId === selectedLicenseId);
    let reqObj = {
      name: name,
      orgNo: orgNo,
      email: email,
      actorLicenseId: selectedLicenseId,
      dataId: selectedLicense.dataId,
      yearOfEducation: yearOfEducation,
      placeOfEducation: placeOfEducation,
      deliveryName: deliveryName,
      street: street,
      zipCode: zipCode,
      city: city,
      token: token,
    };

    try {
      let response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}lostcard/replacement`,
        reqObj,
        "POST",
        false,
        true,
      );
      if (response.isSuccessful) {
        setOrderId(response.orderId);
        setPaymentId(response.paymentId);
        setShowView(LostCardView.PAYMENT);
      } else if (response.errorMessageTranslationKey === "Unauthorized") {
        setShowView(LostCardView.BANK_ID);
        clearStates();
      }
    } catch (error) {
      setShowView(LostCardView.INIT);
    } finally {
      setIsLoading(false);
    }
  }

  async function getLostCardPreview() {
    setIsLoading(true);

    const selectedLicense = actorLicenses.find((l) => l.actorLicenseId === selectedLicenseId);

    let reqObj = {
      actorLicenseId: selectedLicenseId,
      dataId: selectedLicense.dataId,
      token: token,
    };

    try {
      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}lostcard/preview`, reqObj, "POST", false, true);
      if (response.isSuccessful) {
        setPreviewStudentName(response.studentName);
        setPreviewStudentOrgNo(response.studentOrgNo);
        setPreviewValidFrom(response.validFrom);
        setPreviewTheoryCompetence(response.theoryCompetence);
        setPreviewPracticalCompetence(response.practicalCompetence);
        setPreviewTeacherName(response.teacherName);
        setPreviewCourseCoordinator(response.courseCoordinator);
        setShowView(LostCardView.ORDRER_SUMMARY);
      } else if (response.errorMessageTranslationKey === "Unauthorized") {
        setShowView(LostCardView.BANK_ID);
        clearStates();
      }
    } catch (error) {
      setShowView(LostCardView.INIT);
    } finally {
      setIsLoading(false);
    }
  }

  function clearStates() {
    setOrgNo("");
    setEmail("");
    setActorLicenses([]);
    setSelectedLicenseType("choose");
    setSelectedLicenseId(null);
    setEducation("");
    setFreeTextEducation("");
    setYearOfEducation("");
    setPlaceOfEducation("");
    setInstructorName("");
    setEducationProvider("");
    setDeliveryName("");
    setStreet("");
    setZipCode("");
    setCity("");

    setOrderId("");
    setPaymentId("");

    setToken(null);
  }

  function filterLicensesByType(licenses, selectedType) {
    if (selectedType === "choose") {
      return [];
    }

    const filterValue = selectedType.toLowerCase();
    return licenses.filter(({ licenseGroupDescription }) => {
      const desc = licenseGroupDescription.toLowerCase();
      if (filterValue === "truck") {
        return desc === "truck" || desc === "tlp2" || desc === "tlp 2";
      }

      return desc.includes(filterValue);
    });
  }

  async function restartPayment() {
    setIsLoading(true);

    try {
      const response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}orders/lostcard/${orderId}/payment/${paymentId}/restart?token=${encodeURIComponent(token)}`,
        false,
        "GET",
      );
      if (response.isSuccessful) {
        setPaymentId(response.newPaymentId);
        setShowView(LostCardView.PAYMENT);
      }

      if (response.errorMessageTranslationKey === "Unauthorized") {
        setShowView(LostCardView.BANK_ID);
        clearStates();
      }
    } finally {
      setIsLoading(false);
    }
  }

  function handlePaymentReceived() {
    setShowView(LostCardView.ORDER_SUCCESS);
    clearStates();
  }

  async function handlePaymentClick() {
    if (orderId === "") {
      await postLostCardOrder();
    } else {
      setShowView(LostCardView.PAYMENT);
    }
  }

  if (!langLoaded) {
    return <Preloader />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        maxWidth: isSmallScreen ? "" : "80em",
        justifyContent: isSmallScreen ? "" : "center",
        margin: "0 auto",
        padding: "1em",
      }}
    >
      <div style={{ flex: 1 }}>
        {isLoading && <Preloader />}
        <Grid container spacing={2}>
          <Grid size={{ md: 12, xs: 12 }}>
            <img
              src={!isSmallScreen ? "/static/lostcard.png" : "/static/lostCard_mobile.png"}
              alt=""
              style={{
                maxHeight: "200px",
                width: "100%",
                objectFit: "contain",
              }}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <div style={{ maxWidth: "35em", margin: isSmallScreen ? "0 1em" : "0 auto" }}>
              <p style={{ fontWeight: "bold" }}>Välkommen till Tappat intyg</p>
              <p style={{ marginTop: "1em" }}>
                Tappat intyg är en webbtjänst för beställning av ersättningsintyg för borttappade eller skadade
                förarintyg.
              </p>
              <p style={{ marginTop: "1em" }}>Så här fungerar det:</p>
              <ul style={{ marginLeft: "1.2em" }}>
                <li>Logga in med BankID</li>
                <li>Välj det intyg du vill beställa</li>
                <li>Betala med Swish</li>
                <li>Intyget skrivs ut och postas</li>
              </ul>
              <br />
              <p>Ersättningsintyget kostar 350 SEK inkl. moms och frakt.</p>

              <br />
              <p>Tänk på att ditt förarintyg är en värdehandling och bör hanteras som en sådan.</p>
            </div>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} style={{ display: "flex", justifyContent: "center" }}>
            <div>
              {showView === LostCardView.INIT && (
                <Card style={{ width: "20em", height: "15em" }}>
                  <CardContent
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <b>Logga in med BankID</b>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => setShowView(LostCardView.BANK_ID)}>
                      Starta BankID
                    </Button>
                  </CardContent>
                </Card>
              )}

              {showView === LostCardView.BANK_ID && (
                <div>
                  <BankIdAuthentication
                    startAuthenticationUrl={`${import.meta.env.VITE_MAIN_URL}lostcard/bankId/authenticate`}
                    //onStatusInitiated={onStatusInitiated}
                    onAuthenticationSuccess={onAuthenticationSuccess}
                    //onAutheticationFailure={}
                    //startQrCodeInMobile={startQrCodeInMobile}
                  />
                </div>
              )}

              {(showView === LostCardView.FORM || showView === LostCardView.LICENSE) && (
                <Card style={{ padding: "1em", margin: isSmallScreen ? "0.4em" : "" }}>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <b>Personuppgifter</b>
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>Personnummer: {orgNo}</Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        label="Namn"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        label="Email"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>

                    {showView === LostCardView.LICENSE && (
                      <>
                        <Grid size={{ md: 12, xs: 12 }}>
                          <b>Välj ett intyg</b>
                        </Grid>

                        <Grid size={{ md: 12, xs: 12 }}>
                          <FormControl fullWidth>
                            <InputLabel id="licenseType" required>
                              Intygstyp
                            </InputLabel>
                            <Select
                              labelId="licenseType"
                              id="licenseType"
                              value={selectedLicenseType}
                              label="licenseType"
                              onChange={(event) => {
                                setSelectedLicenseType(event.target.value);
                              }}
                            >
                              <MenuItem value={"choose"}>Välj intygstyp</MenuItem>
                              <MenuItem value={"truck"}>Truck</MenuItem>
                              <MenuItem value={"lift"}>Lift</MenuItem>
                              <MenuItem value={"fallskydd"}>Fallskydd</MenuItem>
                              <MenuItem value={"kran"}>Kran</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {filterLicensesByType(actorLicenses, selectedLicenseType).map((license) => (
                          <Grid size={{ md: 6, xs: 12 }} key={license.actorLicenseId}>
                            <div
                              style={{
                                border: `${license.actorLicenseId === selectedLicenseId ? 2 : 1}px solid ${
                                  license.actorLicenseId === selectedLicenseId ? "#1976d2" : "#e0e0e0"
                                }`,
                                borderRadius: "5px",
                                padding: "1em",
                                cursor: "pointer",
                              }}
                              onClick={() => setSelectedLicenseId(license.actorLicenseId)}
                            >
                              <Grid container spacing={1}>
                                <Grid size={{ md: 6, xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Utbildningsdatum
                                  </Typography>
                                  <Typography variant="body1">{formatDate(license.dateOfIssue)}</Typography>
                                </Grid>

                                <Grid size={{ md: 6, xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Teorikompetens
                                  </Typography>
                                  <Typography variant="body1">{license.theory}</Typography>
                                </Grid>

                                <Grid size={{ md: 12, xs: 12 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Praktikkompetens
                                  </Typography>
                                  <Typography variant="body1">{license.practice}</Typography>
                                </Grid>

                                <Grid size={{ md: 6, xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Instruktör
                                  </Typography>
                                  <Typography variant="body1">{license.instructorName}</Typography>
                                </Grid>

                                <Grid size={{ md: 6, xs: 6 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Utbildningsanordnare
                                  </Typography>
                                  <Typography variant="body1">{license.courseCoordinatorName}</Typography>
                                </Grid>
                              </Grid>
                            </div>
                          </Grid>
                        ))}

                        <Grid size={{ md: 12, xs: 12 }}>
                          <div>
                            Hittar du inte intyget du letar efter? Gå till{" "}
                            <span
                              onClick={() => setShowView(LostCardView.FORM)}
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              formuläret
                            </span>{" "}
                            istället
                          </div>
                        </Grid>
                      </>
                    )}

                    {showView === LostCardView.FORM && (
                      <>
                        <Grid size={{ md: 8, xs: 12 }}>
                          <b>Utbildningsinformation</b>
                        </Grid>

                        {actorLicenses.length > 0 && (
                          <Grid size={{ md: 4, xs: 12 }}>
                            <div>
                              Gå tillbaka till{" "}
                              <span
                                onClick={() => {
                                  setShowView(LostCardView.LICENSE);
                                }}
                                style={{
                                  cursor: "pointer",
                                  color: "#1976d2",
                                  textDecoration: "underline",
                                }}
                              >
                                intygen
                              </span>
                            </div>
                          </Grid>
                        )}

                        <Grid size={{ md: 6, xs: 12 }}>
                          <FormControl fullWidth>
                            <InputLabel id="education" required>
                              Utbildning
                            </InputLabel>
                            <Select
                              labelId="Utbildning"
                              id="Utbildning"
                              value={education}
                              label="Education"
                              onChange={(event) => {
                                setEducation(event.target.value);
                                if (event.target.value !== "other") {
                                  setFreeTextEducation("");
                                }
                              }}
                            >
                              <MenuItem value={"choose"}>Välj utbildning</MenuItem>
                              <MenuItem value={"truck"}>Truck</MenuItem>
                              <MenuItem value={"lift"}>Lift</MenuItem>
                              <MenuItem value={"fallskydd"}>Fallskydd</MenuItem>
                              <MenuItem value={"kran"}>Kran/Travers</MenuItem>
                              <MenuItem value={"other"}>Övrig</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                          <TextField
                            value={freeTextEducation}
                            onChange={(event) => setFreeTextEducation(event.target.value)}
                            label="Utbildning fritext (om övrig)"
                            variant="outlined"
                            fullWidth
                            disabled={education !== "other"}
                            required={education === "other"}
                          />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                          <FormControl fullWidth>
                            <InputLabel id="educationYear" required>
                              Utbildningsår
                            </InputLabel>
                            <Select
                              labelId="Utbildningsår"
                              id="educationYear"
                              value={yearOfEducation}
                              label="EducationYear"
                              onChange={(event) => setYearOfEducation(event.target.value)}
                            >
                              <MenuItem value={"choose"}>Välj utbildningsår (ungefärligt)</MenuItem>
                              {years.map((year, index) => {
                                return (
                                  <MenuItem key={index} value={year}>
                                    {year}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                          <TextField
                            value={placeOfEducation}
                            onChange={(event) => setPlaceOfEducation(event.target.value)}
                            label="Utbildningsort"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                          <TextField
                            value={instructorName}
                            onChange={(event) => setInstructorName(event.target.value)}
                            label="Instruktör"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                          <TextField
                            value={educationProvider}
                            onChange={(event) => setEducationProvider(event.target.value)}
                            label="Utbildningsanordnare"
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}
                    <Grid size={{ md: 12, xs: 12 }}>
                      <b>Leveransuppgifter</b>
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={deliveryName}
                        onChange={(event) => setDeliveryName(event.target.value)}
                        label="Namn/co"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={street}
                        onChange={(event) => setStreet(event.target.value)}
                        label="Gata"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={zipCode}
                        onChange={(event) => setZipCode(event.target.value)}
                        label="Postnummer"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }}>
                      <TextField
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                        label="Ort"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>
                      <Grid container justify="flex-end">
                        <Button variant="contained" onClick={handleLostCardSubmit} disabled={disableSendButton()}>
                          {showView === LostCardView.FORM ? "Skicka" : "Gå vidare"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              )}

              {showView === LostCardView.ORDRER_SUMMARY && (
                <>
                  <Card
                    style={{
                      padding: "1em",
                      margin: isSmallScreen ? "0.4em" : "",
                      width: isSmallScreen ? "auto" : "35em",
                      minWidth: "100%",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid size={{ md: 12, xs: 12 }}>
                        <b>Orderöversikt</b>
                      </Grid>

                      <Grid size={{ md: 12, xs: 12 }}>
                        <div
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "5px",
                            padding: "1em",
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Namn
                              </Typography>
                              <Typography variant="body1">{previewStudentName}</Typography>
                            </Grid>

                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Personnummer
                              </Typography>
                              <Typography variant="body1">{previewStudentOrgNo}</Typography>
                            </Grid>

                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Utbildningsdatum
                              </Typography>
                              <Typography variant="body1">{UTCToLocalDateOnly(previewValidFrom)}</Typography>
                            </Grid>

                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Kompetenser
                              </Typography>
                              <Button onClick={() => setOpenCompetencesDialog(true)} variant="outlined">
                                Visa intygets kompetenser
                              </Button>
                            </Grid>

                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Instruktör
                              </Typography>
                              <Typography variant="body1">{previewTeacherName}</Typography>
                            </Grid>

                            <Grid size={{ md: 6, xs: 6 }}>
                              <Typography variant="caption" color="text.secondary">
                                Utbildningsanordnare
                              </Typography>
                              <Typography variant="body1">{previewCourseCoordinator}</Typography>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>

                      <Grid size={{ md: 12, xs: 12 }}>
                        <div>
                          Stämmer det inte? Gå tillbaka till{" "}
                          <span
                            onClick={() => {
                              setShowView(LostCardView.LICENSE);
                              setOrderId("");
                            }}
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              textDecoration: "underline",
                            }}
                          >
                            intygen
                          </span>{" "}
                          och välj ett annat eller använd formuläret
                        </div>
                      </Grid>

                      <Grid size={{ md: 12, xs: 12 }}>
                        <b>Betalning</b>

                        <div
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "5px",
                            marginTop: "1em",
                          }}
                        >
                          <Grid container spacing={1} style={{ padding: "1em" }}>
                            <Grid size={{ md: 6, xs: 6 }}>
                              <RadioGroup value={true}>
                                <FormControlLabel value="true" control={<Radio />} label="Swish" />
                              </RadioGroup>
                            </Grid>
                            <Grid
                              size={{ md: 6, xs: 6 }}
                              sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
                            >
                              <img
                                src="https://eportpublic.blob.core.windows.net/others/swish/swish_logo_secondary_light.svg?sp=r&st=2024-07-22T16:41:04Z&se=2059-07-23T00:41:04Z&spr=https&sv=2022-11-02&sr=b&sig=j9dhNuNrcng%2Fij3UJzNYtRtYfnVg2USwhtB6mH142yw%3D"
                                alt="Credit Card Logo"
                                style={{
                                  height: "32px",
                                  marginLeft: "8px",
                                }}
                              />
                            </Grid>
                          </Grid>
                        </div>

                        <Grid size={{ md: 6, xs: 6 }} style={{ marginTop: "1em" }}>
                          <Button variant="contained" onClick={handlePaymentClick}>
                            Betala 350 kr
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </>
              )}

              {showView === LostCardView.PAYMENT && (
                <PaymentDialog
                  orderId={orderId}
                  paymentId={paymentId}
                  token={token}
                  onClose={() => setShowView(LostCardView.ORDRER_SUMMARY)}
                  onRestartPayment={restartPayment}
                  onPaymentReceived={handlePaymentReceived}
                  flowType={"lostCard"}
                />
              )}

              {showView === LostCardView.SUCCESS && (
                <div style={{ paddingTop: "3em" }}>
                  <Card
                    sx={{
                      backgroundColor: "#f9fff9",
                      marginLeft: isSmallScreen ? "1em" : "",
                      marginRight: isSmallScreen ? "1em" : "",
                    }}
                  >
                    <CardContent sx={{ display: "flex", alignItems: "center" }}>
                      <GridCheckCircleIcon
                        color="success"
                        sx={{
                          fontSize: 40,
                          mr: 2,
                        }}
                      />
                      <div>
                        <Typography variant="h6">Skickad!</Typography>
                        <Typography>Vi letar efter ditt intyg och återkommer.</Typography>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {showView === LostCardView.ORDER_SUCCESS && (
                <div style={{ paddingTop: "3em" }}>
                  <Card
                    sx={{
                      backgroundColor: "#f9fff9",
                      marginLeft: isSmallScreen ? "1em" : "",
                      marginRight: isSmallScreen ? "1em" : "",
                    }}
                  >
                    <CardContent sx={{ display: "flex", alignItems: "center" }}>
                      <GridCheckCircleIcon
                        color="success"
                        sx={{
                          fontSize: 40,
                          mr: 2,
                        }}
                      />
                      <div>
                        <Typography variant="h6">Klart!</Typography>
                        <Typography>Tack för din beställning! Kvittot har skickats till din e-postadress.</Typography>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {showView === LostCardView.NOT_FOUND && (
                <div>
                  <Dialog open={true}>
                    <DialogTitle>Hittades inte</DialogTitle>
                    <DialogContent>
                      <DialogContentText id="alert-dialog-description">
                        Vi kan tyvärr inte hitta ditt personnummer i något av våra register. Tänk på att vi endast kan
                        utfärda intyg som utfärdats av någon av tjänstens samarbetspartners. Du kan fortsätta till
                        formuläret och skicka det, men sannolikheten för att vi ska kunna skicka ett nytt intyg är
                        tyvärr låg.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setShowView(LostCardView.BANK_ID)}>Avbryt</Button>
                      <Button onClick={() => setShowView(LostCardView.FORM)}>Fortsätt</Button>
                    </DialogActions>
                  </Dialog>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </div>

      <footer
        style={{
          marginTop: isSmallScreen ? "4em" : "1em",
          padding: "1em",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "left",
          backgroundColor: "#f9f9f9",
          borderTop: "1px solid #ddd",
          fontSize: "0.9em",
          color: "#555",
        }}
      >
        <p style={{ margin: 0, maxWidth: "90%" }}>
          Detta är en tjänst som administreras av MA-system Utbildning AB. När du skickar en beställning om ett
          ersättningsintyg via detta formulär godkänner du samtidigt att vi får behandla dina personuppgifter för att
          kunna genomföra beställningen. Om du ej har tillgång till BankId, kontakta oss på{" "}
          <a href="mailto:education@masystem.se">education@masystem.se</a>
        </p>
      </footer>

      <Dialog open={openCompetencesDialog} onClose={() => setOpenCompetencesDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Intygets kompetenser</DialogTitle>
        <DialogContent>
          <div>
            <Typography variant="caption" color="text.secondary">
              Teori
            </Typography>
            <Typography variant="body1">
              {previewTheoryCompetence
                .sort((a, b) => a.shortCode.localeCompare(b.shortCode))
                .map((tc) => (
                  <div key={tc.shortCode}>
                    {tc.shortCode} - {tc.description}
                  </div>
                ))}
            </Typography>
          </div>
          <div style={{ marginTop: "1em" }}>
            <Typography variant="caption" color="text.secondary">
              Praktik
            </Typography>
            <Typography variant="body1">
              {previewPracticalCompetence
                .sort((a, b) => a.shortCode.localeCompare(b.shortCode))
                .map((tc) => (
                  <div key={tc.shortCode}>
                    {tc.shortCode} - {tc.description}
                  </div>
                ))}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompetencesDialog(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default LostCard;
